import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface JobDetailJsonProps {
  input: unknown;
  result: unknown;
}

interface JsonBlockProps {
  title: string;
  value: unknown;
}

const JsonBlock = (props: JsonBlockProps) => {
  const { title, value } = props;
  const text = JSON.stringify(value ?? {}, null, 2);

  const handleCopy = () => {
    void navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          color="text.secondary"
          sx={{
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
          variant="caption"
        >
          {title}
        </Typography>
        <Button
          onClick={handleCopy}
          size="small"
        >
          Copy
        </Button>
      </Stack>
      <Box
        component="pre"
        sx={{
          bgcolor: "action.hover",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: 12.5,
          lineHeight: 1.55,
          m: 0,
          maxHeight: 280,
          overflow: "auto",
          p: 1.5,
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

/** A job's raw input and result, folded away under "Input and result JSON", each with a Copy button. */
export const JobDetailJson = (props: JobDetailJsonProps) => {
  const { input, result } = props;
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((current) => !current);
  };

  return (
    <Box
      sx={{
        borderColor: "divider",
        borderTop: 1,
        pt: 1.5,
      }}
    >
      <Button
        aria-expanded={isOpen}
        color="inherit"
        onClick={handleToggle}
        size="small"
        startIcon={(
          <ChevronRightIcon
            sx={{
              transform: isOpen ? "rotate(90deg)" : "none",
              transition: "transform 160ms",
            }}
          />
        )}
        sx={{ color: "text.secondary" }}
      >
        Input and result JSON
      </Button>
      <Collapse in={isOpen}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 2fr)" },
            mt: 1.5,
          }}
        >
          <JsonBlock
            title="Input"
            value={input}
          />
          <JsonBlock
            title="Result"
            value={result}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
